import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Divider } from 'antd';
import type { TourProps } from 'antd';
import {VacancyCardEmployer} from '../Components/VacancyCards';
import emptyCompanyPhoto from '../images/emptyCompanyPhoto.svg';
import { GreenAlert as Alert } from '../Components/Alerts/GreenAlert';
import usePortalService from '../services/PortalService';
import { CompanyDataIsNotFilled } from '../Components/Modals/CompanyDataIsNotFilled';
import ImagesViewer from '../Components/ImageElements/ImagesViewer';
import { BlueAlert } from '../Components/Alerts/BlueAlert';
import { TextPreparator } from '../utils/TextPreparator';
import { ManagerNavbar } from '../Components/Navbars';
import { Vacancy } from '../types';
import { LoaderWithTimer } from '../Components/LoaderWithTimer';
import { UserTour } from '../Components/UserTour';

export const HomeEmployer = () => {
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [link, setLink] = useState('');
    const [linkLabel, setLinkLabel] = useState('');
    const [description, setDescription] = useState('');
    const [vacancies, setVacancies] = useState<Vacancy[]>([]);
    const [imagesIds, setImageIds] = useState([]);

    const [registrationTelegramLink, setRegistrationTelegramLink] = useState('');
    const [isRegisteredTelegramBot, setIsRegisteredTelegramBot] = useState(true);

    const [isManagerTiedToCompany, setIsManagerTiedToCompany] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const navigate = useNavigate();
    const { mainInformation } = usePortalService();

    const addVacancyButtonRef = useRef(null);
    
    const steps: TourProps['steps'] = [
        {
            title: 'О компании',
            description: 'Нажмите на аватарку, и в выпадающем меню выберите "О компании", чтобы добавить данные о компании или отредактировать их',
            target: () => document.querySelector('.ant-avatar')!
        },
        {
            title: 'Создание вакансии',
            description: 'Нажмите сюда, чтобы создать новую вакансию',
            target: () => addVacancyButtonRef.current
        },
        {
            title: 'Вакансии',
            description: 'По этой ссылке можно посмотреть все вакансии вашей компании и отклики кандидатов',
            target: () => document.getElementById('vacancies')!
        },
        {
            title: 'Кандидаты',
            description: 'Здесь можно посмотреть список всех кандидатов',
            target: () => document.getElementById('candidates')!
        },
        {
            title: 'События',
            description: 'Здесь можно создать и посмотреть события',
            target: () => document.getElementById('events')!
        },
        {
            title: 'Рейтинг студентов',
            description: 'По этой ссылке можно посмотреть рейтинг лучших студентов',
            target: () => document.getElementById('ratingStudents')!
        }, 
        {
            title: 'Чат',
            description: 'Здесь в чате вы можете увидеть отклики и сообщения от кандидатов',
            target: () => document.getElementById('chatNav')!
        }
    ]

    useEffect(() => {
        setIsLoading(true)
        mainInformation().then(result => {
            if (result.company != null) {
                setIsManagerTiedToCompany(true);
                setName(result.company.name);
                setAddress(result.company.address);
                setLinkLabel(result.company.link);
                if (result.company.link.includes("http")) {
                    setLink(result.company.link);
                } else {
                    setLink("https://"+result.company.link);
                }
                setDescription(TextPreparator.prepare(result.company.description));
                setVacancies(result.company.vacancies);
                setImageIds(result.company.imagesIds);
                setRegistrationTelegramLink(result.manager.registrationTelegramLink);
                setIsRegisteredTelegramBot(result.manager.isRegisteredTelegramBot);
            }
            else {
                setName('О компании');
                setDescription('Для продолжения работы заполните информацию о своей компании в личном кабинете ');
            }
        }).catch((error) => {
            setHasError(true);
            console.log(error)
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const createVacancyHandler = async () => {
        if (isManagerTiedToCompany) {
            navigate("/createVacancy");
        } else {
            showModal();
        }
    }

    const showModal = () => {
      setIsModalOpen(true);
    };
  
    const closeModal = () => {
      setIsModalOpen(false);
    };

    const closeAlert = () => {
        sessionStorage.removeItem('email');
        sessionStorage.removeItem('headingAlert')
    }

    return (
        <>
            {(sessionStorage.getItem('headingAlert')) && 
                <Alert 
                    heading={sessionStorage.getItem('headingAlert') || ''} 
                    message={"Подтвердите эл. почту, перейдя по ссылке в письме. Мы выслали Вам подтверждение на "+sessionStorage.getItem('email')+"."}
                    onClose={closeAlert}
                />
            }
            {(!isRegisteredTelegramBot) &&
                <BlueAlert 
                    linkTelegram={registrationTelegramLink}
                    message="Там Вы сможете вести переписку с кандидатами" 
                />
            }
            <ManagerNavbar />
            <div className="left-page-container">
                <div className="left-page-content">
                    {!isLoading ? 
                        <>
                            <h1 style={{ paddingBottom: '8px' }}> {name} </h1>
                            <h2 className="address" style={{ paddingBottom: '4px' }}> {address} </h2>
                            <a href={link} className="gray-link" target="_blank" rel='noreferrer'> {linkLabel} </a>
                            <div 
                                dangerouslySetInnerHTML={{ __html: description }} 
                                style={{ paddingTop: '16px', maxWidth: '86.8%' }}
                            />
                            <Divider />
                            <div className="pair-text-button">
                                <h3 className="text-in-pair"> Активные вакансии и стажировки компании </h3>
                                <div className="button-in-pair">
                                    <Button ref={addVacancyButtonRef} variant="primary" onClick={createVacancyHandler} style={{height: '32px', width:'108px'}}> + Создать </Button>
                                </div>
                            </div>
                            {(vacancies.length == 0) && <p> У вас пока нет активных вакансий. </p>}
                            {vacancies.map(vacancy => (
                                <VacancyCardEmployer
                                    vacancy={vacancy} 
                                    key={vacancy.id}
                                />
                            ))}
                        </> : 
                        <LoaderWithTimer isLoading={isLoading} skeletonStyle="mainInformation"/>
                    }
                    {hasError && <p>Произошла ошибка при загрузке. Пожалуйста, попробуйте обновить страницу.</p>}
                </div>
                <div className="right-page-content d-none d-md-block">
                    {(imagesIds?.length > 0) ? 
                    <ImagesViewer imagesIds={imagesIds} type={'company'}/> :
                    <a href="/editProfileCompany"><img src={emptyCompanyPhoto} /></a>}
                </div>
            </div>
            <CompanyDataIsNotFilled isOpen={isModalOpen} close={closeModal}/>
            <UserTour steps={steps} pageKey={'homeEmployer'} />
        </>
    );
};